import { FC, HTMLProps, Ref, useMemo, lazy, Suspense } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { AIChatbotResponse } from '../../types';
import { MessageFeedback } from './message-feedback';

const ReactMarkdown = lazy(() => import('react-markdown'));

interface ChatMessageProps {
  chatbotResponse: AIChatbotResponse;
  innerRef?: Ref<HTMLDivElement>;
}

export const ChatMessage: FC<ChatMessageProps> = ({ chatbotResponse, innerRef }) => {
  const { t } = useTranslation('aiOnboarding');
  const messageList = useMemo(() => chatbotResponse.message.content.split('\n'), [chatbotResponse.message.content]);
  if (chatbotResponse.role === 'assistant') {
    return (
      <div css={styles.assistantMessage} ref={innerRef}>
        <Suspense fallback={<div>{t('Loading...')}</div>}>
          <ReactMarkdown components={{ a: LinkRenderer }}>{messageList.join('\n')}</ReactMarkdown>
          <MessageFeedback messageId={chatbotResponse.id} />
        </Suspense>
      </div>
    );
  }
  return (
    <Text as='div' css={styles.userMessage} size='medium' ref={innerRef}>
      {messageList.map((msg, index) => (
        <div key={index}>{msg}</div>
      ))}
    </Text>
  );
};

const LinkRenderer: React.FC<HTMLProps<HTMLAnchorElement>> = (props) => {
  return (
    <a href={props.href} target='_blank' rel='noreferrer noopener'>
      {props.children}
    </a>
  );
};

const styles = {
  userMessage: css`
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.large};
    padding: ${theme.spacing(1, 2)};
    margin-left: auto;
  `,
  assistantMessage: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    background-color: ${theme.colors.primary5};
    padding: ${theme.spacing(1, 2)};
    border-radius: ${theme.borderRadius.large};
    width: fit-content;
  `,
};
